import Battery0BarIcon from '@mui/icons-material/Battery0Bar';
import Battery1BarIcon from '@mui/icons-material/Battery1Bar';
import Battery2BarIcon from '@mui/icons-material/Battery2Bar';
import Battery3BarIcon from '@mui/icons-material/Battery3Bar';
import Battery4BarIcon from '@mui/icons-material/Battery4Bar';
import Battery5BarIcon from '@mui/icons-material/Battery5Bar';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import BatteryUnknownIcon from '@mui/icons-material/BatteryUnknown';
import CancelIcon from '@mui/icons-material/Cancel';
import CellTowerIcon from '@mui/icons-material/CellTower';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { SvgIcon } from '@mui/material';
import { add, isBefore } from 'date-fns';
import { find, isNil, omit } from 'lodash';
import { ReactElement, useCallback, useMemo } from 'react';

import { getDevices, useDevices } from '@/api/devices/getDevices';
import { useOceansDevices } from '@/api/oceansApi/useOceansDevices';
import { useOceansLatestData } from '@/api/oceansApi/useOceansLatestData';
import { SpotterTelemetryCapabilities, useSubscriptionTagsInfo } from '@/api/subscriptionTags';
import { useUserAccount } from '@/api/userAccount';
import { QueryConfig, pickQueryStatus } from '@/common/react-query';
import { useOptionalSpotterContext, useSpotterContext } from '@/context/SpotterContext';
import { useNow } from '@/hooks/useNow';
import { isVersionGte, showUiElementPerIridiumVar } from '@/utils';

import {
    CellularSignalMetric,
    Device,
    DeviceHullType,
    DeviceLatestData,
    GetDevicesResult,
    SpotterSystemError,
} from './types';

export type UseDeviceOptions<TSelected = Device | undefined> = {
    spotterId?: string | null;
    /** The query's own `select` runs over the whole list, so narrowing to one device goes through `select` below. */
    config?: Omit<QueryConfig<typeof getDevices>, 'select'>;
    /**
     * Narrows what the caller subscribes to. Runs inside react-query rather than on the returned
     * data, so a consumer reading one field re-renders only when that field changes, not on every
     * `devices-list` invalidation.
     *
     * Hold it stable, at module scope or through `useCallback`: react-query memoizes the selection
     * against this function's identity, so a fresh one on each render re-runs the selection, and the
     * deep comparison that follows it, for every consumer on every render.
     */
    select?: (device: Device | undefined) => TSelected;
};

export type UseDeviceNameResult = {
    data?: { spotterId: string; name?: string | null; hullType?: DeviceHullType };
    isLoading: boolean;
    isSuccess: boolean;
    isError: boolean;
    isPending: boolean;
    error: Error | null;
};

/**
 * Gets SpotterContext-aware device display name, and normalizes the "not found" error if the context spotterId is not found.
 *
 * Will use either oceans-api/devices or fetch/devices as appropriate.
 */
export const useDeviceName = ({
    config: { enabled = true, ...config } = {},
}: UseDeviceOptions = {}): UseDeviceNameResult => {
    const { spotterId, viaOceansApi } = useSpotterContext();

    const fetchResult = useDevice({
        spotterId,
        config: { ...config, enabled: enabled && !viaOceansApi },
    });

    const oceansResult = useOceansDevices({
        config: { enabled: !!spotterId && enabled && viaOceansApi },
    });

    if (viaOceansApi) {
        const device = oceansResult.data?.data.find((d) => d.spotterId === spotterId);
        const notFound = oceansResult.isSuccess && !device;

        return {
            ...pickQueryStatus(oceansResult),
            isSuccess: oceansResult.isSuccess && !notFound,
            isError: oceansResult.isError || notFound,
            error: oceansResult.error ?? (notFound ? new Error('Spotter not found') : null),
            data: device,
        };
    }

    return fetchResult;
};

/**
 * Gets SpotterContext-aware latest data, essentially a subset of the fields supplied by useDevice.
 *
 * Will use either oceans-api/latest-data or fetch/devices as appropriate.
 */
export const useDeviceLatestData = (): { isPending: boolean; device?: DeviceLatestData } => {
    const { spotterId, viaOceansApi, spotterFamily } = useSpotterContext();

    const { data: deviceResult, isPending: devicesPending } = useDevice({
        config: { enabled: !viaOceansApi },
    });

    const { data: latestDataResult, status: latestStatus } = useOceansLatestData({
        spotterFamily,
        config: { enabled: viaOceansApi },
    });

    const { device, isPending } = useMemo(() => {
        if (!spotterId) {
            return { isPending: true, device: undefined };
        }

        if (!viaOceansApi) {
            return {
                isPending: devicesPending,
                device: deviceResult,
            };
        }

        const latestSpotterData = find(latestDataResult?.data, { spotterId });

        return {
            isPending: latestStatus === 'pending',
            device: latestSpotterData,
        };
    }, [deviceResult, devicesPending, latestDataResult, latestStatus, spotterId, viaOceansApi]);

    return { device, isPending };
};

export const useDevice = <TSelected = Device | undefined,>({
    spotterId: spotterIdProp,
    config: { enabled = true, ...config } = {},
    select,
}: UseDeviceOptions<TSelected> = {}) => {
    const { spotterId } = useOptionalSpotterContext({ spotterId: spotterIdProp });

    // Picking the single device is itself part of the selection, so it runs inside react-query and a
    // caller narrowing further never sees a fresh object built after the fact.
    const selectDevice = useCallback(
        (devices: GetDevicesResult): TSelected => {
            const device = devices?.[0];
            // Without a `select`, TSelected is its default of `Device | undefined`; a defaulted type
            // parameter cannot state that identity, so the compiler cannot prove it here.
            return select ? select(device) : (device as TSelected);
        },
        [select]
    );

    return useDevices<TSelected>({
        devices: [{ spotterId: spotterId! }],
        config: { ...config, enabled: !!spotterId && enabled, select: selectDevice },
    });
};

const DEFAULT_HDR_PROCESSING_RATE_MINS = 30;

const selectHdrProcessingRateMins = (device: Device | undefined): number =>
    device?.cellularData?.cellularSettings?.processingRateMins ?? DEFAULT_HDR_PROCESSING_RATE_MINS;

/**
 * How often the device's cellular firmware processes HDR data, which the historical-data hooks need
 * to space HDR samples.
 *
 * Selected inside the query rather than read off the device, so a chart depending on this rate
 * re-renders when the rate changes rather than on every `devices-list` invalidation — and wavelink
 * invalidates that on nearly every message.
 */
export const useHdrProcessingRateMins = (options: UseDeviceOptions<number> = {}): number => {
    const { data } = useDevice({ ...options, select: selectHdrProcessingRateMins });

    return data ?? DEFAULT_HDR_PROCESSING_RATE_MINS;
};

export const MAX_BATTERY_LEVEL = 4;

const BATTERY_STATUSES = [
    { max: 2, level: 1, StatusIcon: Battery0BarIcon, color: 'error', description: 'Battery is nearly empty' },
    { max: 3.3, level: 1, StatusIcon: Battery1BarIcon, color: 'error', description: 'Battery is nearly empty' },
    { max: 3.6, level: 2, StatusIcon: Battery2BarIcon, color: 'warning', description: 'Battery is low' },
    { max: 3.75, level: 2, StatusIcon: Battery3BarIcon, color: 'warning', description: 'Battery is low' },
    { max: 3.9, level: 3, StatusIcon: Battery4BarIcon, color: 'success', description: 'Battery is OK' },
    { max: 4, level: 3, StatusIcon: Battery5BarIcon, color: 'success', description: 'Battery is OK' },
    { max: Number.MAX_VALUE, level: 4, StatusIcon: BatteryFullIcon, color: 'success', description: 'Battery is full' },
] as const;

export type DeviceBatteryStatus = {
    /**
     * 0-4 empty-full
     */
    level: number;
    batteryVoltage: number;
    batteryPower: number;
    solarVoltage: number;
    StatusIcon: typeof SvgIcon;
    color: (typeof BATTERY_STATUSES)[number]['color'];
    description: string;
    timestamp: string | undefined;
    error: SpotterSystemError | null;
};

export const useDeviceBatteryStatus = (options: UseDeviceOptions) => {
    const { data: device, isPending, isLoading, isError, isSuccess } = useDevice(options);
    const { batteryVoltage, batteryPower, solarVoltage } = device ?? {};
    const status = useMemo(() => {
        if (isNil(batteryVoltage)) {
            return {
                max: null,
                level: null,
                StatusIcon: BatteryUnknownIcon,
                color: 'error',
                description: 'Battery status is unknown',
            };
        }
        return omit(BATTERY_STATUSES.find(({ max }) => batteryVoltage < max)!, 'max');
    }, [batteryVoltage]);

    // legacy Spotters report their error state much more infrequently compared to newer ones
    // to avoid showing possibly stale/errant low battery alerts, we're going to only rely on the most recent battery
    // level to tell users whether the battery is low
    const mostRecentBatteryError: SpotterSystemError | null = useMemo(() => {
        if (!device || !status) {
            return null;
        }

        if (isNil(status.level)) {
            return {
                code: 'NA',
                description: 'Battery status is unknown',
                errorTimestamp: device.timeLastUpdatedUTC ?? '0',
            };
        }
        const hasLowVoltage = status.level <= 2;
        return hasLowVoltage
            ? {
                  code: 'WARN_LOW',
                  description: 'Low battery',
                  errorTimestamp: device.timeLastUpdatedUTC ?? '0',
              }
            : null;
    }, [device, status]);

    return useMemo(
        () => ({
            ...(device && {
                data: {
                    batteryVoltage,
                    batteryPower,
                    solarVoltage,
                    timestamp: device.timeLastUpdatedUTC ?? undefined,
                    error: mostRecentBatteryError,
                    ...status,
                } as DeviceBatteryStatus,
            }),
            isPending,
            isLoading,
            isError,
            isSuccess,
        }),
        [
            batteryPower,
            batteryVoltage,
            device,
            isError,
            isLoading,
            isPending,
            isSuccess,
            solarVoltage,
            mostRecentBatteryError,
            status,
        ]
    );
};

export const MAX_CELLULAR_SIGNAL_LEVEL = 3;

export const CELLULAR_SIGNAL_WEAK_THRESHOLD = -90;
export const CELLULAR_SIGNAL_POOR_SIGNAL_THRESHOLD = -130;
const CELL_MEMORY_FULL_RECOVERABLE_FW_VERSION = '6.2.4';

const CELLULAR_SIGNAL_STATUSES = [
    {
        min: CELLULAR_SIGNAL_WEAK_THRESHOLD,
        level: 3,
        color: 'success',
        description: 'Good',
    },
    {
        min: CELLULAR_SIGNAL_POOR_SIGNAL_THRESHOLD,
        level: 2,
        color: 'warning',
        description: 'Weak',
    },
    {
        min: -Infinity,
        level: 1,
        color: 'error',
        description: 'Poor',
    },
] as const;

export type DeviceCellularSignalStatus = {
    /**
     * 0-3 low-high signal strength
     */
    level: number;
    StatusIcon: typeof SvgIcon;
    color: (typeof CELLULAR_SIGNAL_STATUSES)[number]['color'];
    description: string;
    timestamp: string | undefined;
    error: SpotterSystemError | null;
};

const getCellularRSRPValue = (cellularSignalMetric?: CellularSignalMetric | null): number | null => {
    if (!cellularSignalMetric) {
        return null;
    }
    if (cellularSignalMetric.schema_version === 1) {
        return cellularSignalMetric.card_wireless?.rsrp;
    }
    if (cellularSignalMetric.schema_version === 2) {
        return cellularSignalMetric.rsrp;
    }
    return null;
};

export const useDeviceCellularSignalStatus = (options?: UseDeviceOptions) => {
    const { data: device, isPending, isLoading, isError, isSuccess } = useDevice(options);
    const { cellularData } = device ?? {};
    const signalStrength = getCellularRSRPValue(cellularData?.latestCellularSignalMetric);
    const timestamp = cellularData?.latestCellularSignalMetric?.timestamp;

    const error = useMemo(() => {
        if (!device) {
            return null;
        }

        if (!device.systemErrors.cellular || device.lastMessageCommSource === 'cellular') {
            return null;
        }

        // notecard-x.x.x(.x)? => x.x.x
        const notecardFwVersion = device.cellularData?.metadata?.firmware_version?.match(/\d+\.\d+\.\d+/)?.[0];
        if (
            device.systemErrors.cellular.code === 'WARN_CELL_MEMORY_FULL' &&
            notecardFwVersion &&
            isVersionGte(notecardFwVersion, CELL_MEMORY_FULL_RECOVERABLE_FW_VERSION)
        ) {
            return null;
        }

        return device.systemErrors.cellular;
    }, [device]);

    const status = useMemo(() => {
        if (isNil(signalStrength)) {
            return {
                StatusIcon: CellTowerIcon,
                color: 'error',
                description: 'Unknown',
                level: 0,
            };
        }
        return omit(CELLULAR_SIGNAL_STATUSES.find(({ min }) => signalStrength > min)!, 'min');
    }, [signalStrength]);

    return useMemo(
        () => ({
            ...(device && {
                data: {
                    StatusIcon: CellTowerIcon,
                    timestamp,
                    error,
                    ...status,
                } as DeviceCellularSignalStatus,
            }),
            isPending,
            isLoading,
            isError,
            isSuccess,
        }),
        [device, timestamp, isError, isLoading, isPending, isSuccess, status, error]
    );
};

export type DeviceSignalStatus = {
    lastUpdated: Date | null;
    lastUpdateSource: SpotterTelemetryCapabilities | null;
    isLateToCheckin: boolean;
};

export type DeviceSubscriptionStatus = {
    satCapableButNoService: boolean;
    cellCapableButNoService: boolean;
    cellEnabled: boolean;
    satEnabled: boolean;
    /**
     * This is specifically that we think the reason sat might be disabled is because they ran out
     * of credits:
     * - They have zero credits
     * - AND they are paying for more sat lines than are currently enabled
     */
    outOfSatelliteCredits: boolean;
};

export const useDeviceSignalStatus = (options?: UseDeviceOptions) => {
    const { data: device, isPending, isLoading, isError, isSuccess } = useDevice(options);

    const now = useNow(60);
    const data = useMemo(() => {
        if (!device) {
            return undefined;
        }

        const lastUpdateTimestamp = device.lastMessageTime ?? device?.timeLastUpdatedUTC;
        const lastUpdated = lastUpdateTimestamp ? new Date(lastUpdateTimestamp) : null;
        const lastUpdateSource = device.lastMessageCommSource ?? device?.lastUpdateCommSource ?? null;

        const isLateToCheckin = Boolean(
            device.currentMessagePeriod &&
                lastUpdated &&
                isBefore(add(lastUpdated, { hours: device.currentMessagePeriod * 2 }), now)
        );

        const data: DeviceSignalStatus = {
            lastUpdated,
            lastUpdateSource,
            isLateToCheckin,
        };
        return data;
    }, [device, now]);

    return useMemo(() => {
        return {
            data,
            isPending,
            isLoading,
            isError,
            isSuccess,
        };
    }, [data, isError, isLoading, isPending, isSuccess]);
};

const selectSystemConfigurations = (device: Device | undefined) => device?.systemConfigurations;

export const useDeviceSystemConfigurations = (options: Omit<UseDeviceOptions, 'select'> = {}) =>
    useDevice({ ...options, select: selectSystemConfigurations });

const selectSystemErrors = (device: Device | undefined) => device?.systemErrors;

export const useDeviceSystemErrors = (options: Omit<UseDeviceOptions, 'select'> = {}) =>
    useDevice({ ...options, select: selectSystemErrors });

export const useDeviceSubscriptionStatus = (options?: UseDeviceOptions) => {
    const { spotterId } = useOptionalSpotterContext({ spotterId: options?.spotterId });
    const { data: subscriptionTags, isPending, isLoading, isError, isSuccess } = useSubscriptionTagsInfo();
    const { data: device } = useDevice(options);
    const { data: accountData } = useUserAccount();
    const accountIridiumVar = accountData?.data?.user?.accountIridiumVar;

    const subscription = useMemo(
        () => subscriptionTags?.data.find((d) => d.spotter_id === spotterId),
        [spotterId, subscriptionTags?.data]
    );

    const data = useMemo(() => {
        if (!subscription) {
            return undefined;
        }

        const varSupportsDisabling = showUiElementPerIridiumVar(
            accountIridiumVar,
            'varSupportsDisablingSatelliteTelemetry',
            'every'
        );

        const satCapableButNoService = Boolean(
            subscription?.is_iridium_allowed &&
                subscription.telemetry_capabilities.includes('iridium') &&
                varSupportsDisabling &&
                !subscription.should_iridium_be_active
        );

        const cellCapableButNoService = Boolean(
            subscription?.is_cellular_allowed &&
                device?.externalHardware.cellular &&
                subscription.telemetry_capabilities.includes('cellular') &&
                !subscription.should_cellular_be_active
        );

        // TODO https://app.shortcut.com/sofar/story/227368
        const outOfSatelliteCredits = false;

        const data: DeviceSubscriptionStatus = {
            satCapableButNoService,
            cellCapableButNoService,
            outOfSatelliteCredits,
            cellEnabled: subscription.should_cellular_be_active,
            satEnabled: !varSupportsDisabling || subscription.should_iridium_be_active,
        };
        return data;
    }, [accountIridiumVar, device?.externalHardware.cellular, subscription]);

    return useMemo(() => {
        return {
            data,
            isPending,
            isLoading,
            isError,
            isSuccess,
        };
    }, [data, isError, isLoading, isPending, isSuccess]);
};

const SD_CARD_ERROR_CODES = [
    'BEGIN_FAIL',
    'ERR_INIT_ERR',
    'SETUP_DIR_FAIL',
    'RW_TEST_FAIL',
    'CAP_CHECK_FAIL',
    'CARD_FAIL',
    'MAX_ERR',
    'CARD_FULL',
    'WARN_FULL',
    'CARD_MISSING',
    'ERR_CARD_MISSING',
    'ERR_MOUNT_ERR',
    'ERR_WRTEST_ERR',
];

const SD_CARD_WARNING_CODES = ['WARN_LOW'];

export type DeviceSdCardStatus = {
    status: 'disabled' | 'enabled';
    severity: 'success' | 'warning' | 'error' | 'info';
    label: string;
    timestamp: string | undefined;
    StatusIcon: ReactElement;
};

export const useDeviceSdCardStatus = (
    options: UseDeviceOptions
): { data?: DeviceSdCardStatus; isPending: boolean; isLoading: boolean; isError: boolean; isSuccess: boolean } => {
    const { data: device, isPending, isLoading, isError, isSuccess } = useDevice(options);

    const data = useMemo(() => {
        if (!device) {
            return undefined;
        }

        const { systemConfigurations, systemErrors } = device;
        const isSdCardDisabled = systemConfigurations.sdCard === 'disabled';
        const isSdCardIgnored = systemConfigurations.sdCard === 'ignored';

        if (isSdCardDisabled) {
            return {
                status: 'disabled' as const,
                severity: 'success' as const,
                label: 'Disabled',
                timestamp: device.timeLastUpdatedUTC ?? undefined,
                StatusIcon: <CancelIcon color="disabled" />,
            };
        }

        if (isSdCardIgnored || !systemErrors.sdCard) {
            return {
                status: 'enabled' as const,
                severity: 'success' as const,
                label: 'OK',
                timestamp: device.timeLastUpdatedUTC ?? undefined,
                StatusIcon: <CheckCircleIcon color="success" />,
            };
        }

        const sdCardError = systemErrors.sdCard;
        if (SD_CARD_ERROR_CODES.includes(sdCardError.code)) {
            return {
                status: 'enabled' as const,
                severity: 'error' as const,
                label: sdCardError.description.replace('Unexpected', '').trim(),
                timestamp: sdCardError.errorTimestamp,
                StatusIcon: <ErrorOutlineIcon color="error" />,
            };
        }

        if (SD_CARD_WARNING_CODES.includes(sdCardError.code)) {
            return {
                status: 'enabled' as const,
                severity: 'warning' as const,
                label: sdCardError.description,
                timestamp: sdCardError.errorTimestamp,
                StatusIcon: <WarningAmberIcon color="warning" />,
            };
        }

        // Fallback for any unhandled/new codes
        return {
            status: 'enabled' as const,
            severity: 'info' as const,
            label: sdCardError.description,
            timestamp: sdCardError.errorTimestamp,
            StatusIcon: <InfoOutlineIcon color="info" />,
        };
    }, [device]);

    return useMemo(
        () => ({
            data,
            isPending,
            isLoading,
            isError,
            isSuccess,
        }),
        [data, isError, isLoading, isPending, isSuccess]
    );
};
